import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { researchSessionStore } from 'shared/stores/researchSessionStore'
import {
	normalizeShortlistQuery,
	parseCountries,
} from 'shared/utils/normalizeAiToolsProfile'
import {
	createShortlist,
	getAllCoursesFromShortlistUni,
	getAllShortlisters,
	getShortlisterById,
	getSortShortlisterById,
} from 'api/shortlister'

const DEFAULT_SORT_BY = 'deadline'
const DEFAULT_SORT_ORDER = 'desc'

const SHORTLIST_FILTER_DEFAULTS = {
	countries: ['USA'],
	highestLevel: 'HighSchool',
	highestLevelOfEducation: 'HighSchool',
	nameOfLocation: '',
	selectedPlan: '$30K-$50K/year',
	degreeTitle: 'General',
}

const SORT_OPTIONS = [
	{
		label: 'Deadline (Delayed First)',
		sortBy: 'deadline',
		sortOrder: 'desc',
	},
	{
		label: 'Tuition (Low to High)',
		sortBy: 'fees',
		sortOrder: 'asc',
	},
	{
		label: 'Tuition (High to Low)',
		sortBy: 'fees',
		sortOrder: 'desc',
	},
	{
		label: 'Ranking (Low to High)',
		sortBy: 'rank',
		sortOrder: 'asc',
	},
	{
		label: 'Ranking (High to Low)',
		sortBy: 'rank',
		sortOrder: 'desc',
	},
	{
		label: 'Deadline (Earliest First)',
		sortBy: 'deadline',
		sortOrder: 'asc',
	},
]

const EMPTY_DRAFT_DEFAULTS = {}

/** FilterNav Degree Level values: HighSchool = Bachelors UI, Bachelors = Masters/PhD. */
function withDegreeLevelForShortlist(query = {}) {
	const raw = String(
		query.highestLevelOfEducation ||
			query.highestLevel ||
			SHORTLIST_FILTER_DEFAULTS.highestLevelOfEducation,
	).trim()
	const highestLevelOfEducation =
		raw.toLowerCase() === 'highschool' ||
		raw.toLowerCase() === 'high_school' ||
		raw.toLowerCase() === 'high school'
			? 'HighSchool'
			: 'Bachelors'
	return {
		...query,
		highestLevel: highestLevelOfEducation,
		highestLevelOfEducation,
	}
}

const parseShortlistQuery = query => {
	if (!query) return {}
	if (typeof query === 'string') {
		try {
			return JSON.parse(query) || {}
		} catch {
			return {}
		}
	}
	if (typeof query === 'object') return query
	return {}
}

const filtersFromShortlistQuery = query =>
	normalizeShortlistQuery(parseShortlistQuery(query), { isStudent: false })

/**
 * Build create-shortlist payload from Research draft + required API defaults.
 */
export function buildShortlistPayloadFromDraft(
	draft = {},
	{ hasSelectedCourse = false } = {},
) {
	const category = draft.category || ''
	const subCategory = draft.subCategory || ''
	const career = draft.career || ''
	const course = draft.course || ''
	const countries = parseCountries(draft.countries || draft.country)
	const highestLevel = draft.highestLevel || SHORTLIST_FILTER_DEFAULTS.highestLevel
	const degreeTitle =
		hasSelectedCourse && course
			? course
			: course || subCategory || SHORTLIST_FILTER_DEFAULTS.degreeTitle

	return normalizeShortlistQuery(
		withDegreeLevelForShortlist({
			...SHORTLIST_FILTER_DEFAULTS,
			...draft,
			countries: countries.length
				? countries
				: SHORTLIST_FILTER_DEFAULTS.countries,
			highestLevel,
			highestLevelOfEducation:
				highestLevel === 'HighSchool' ? 'HighSchool' : 'Bachelors',
			category,
			subCategory,
			career,
			course,
			step_1: category,
			step_2: subCategory,
			step_3: career,
			degreeTitle,
			specialization: course || career || '',
			nameOfLocation:
				draft.nameOfLocation || SHORTLIST_FILTER_DEFAULTS.nameOfLocation,
			selectedPlan: draft.selectedPlan || SHORTLIST_FILTER_DEFAULTS.selectedPlan,
			listOfSpecializations: career ? [{ name: career }] : [],
		}),
		{ isStudent: false },
	)
}

const hasSeedDefaults = draft =>
	Boolean(
		draft?.category ||
			draft?.subCategory ||
			draft?.country ||
			(Array.isArray(draft?.countries) && draft.countries.length > 0),
	)

export function useResearchUniversity({
	selectedStudentId,
	activeScreen,
	hasFinderSelection = false,
	hasSelectedCourse = false,
}) {
	const [, setLastShortlistId] = useState(null)
	const [currentListId, setCurrentListId] = useState(null)
	const [newData, setNewData] = useState(SHORTLIST_FILTER_DEFAULTS)
	const [fullList, setFullList] = useState([])
	const [loadingUniversities, setLoadingUniversities] = useState(false)
	const [universitiesError, setUniversitiesError] = useState(null)
	const [searchDisabled, setSearchDisabled] = useState(true)
	const [isDisableSortBtn, setIsDisableSortBtn] = useState(false)
	const [isButtonDisabled, setIsButtonDisabled] = useState(true)
	const [currentPage, setCurrentPage] = useState(1)
	const [totalPages, setTotalPages] = useState(1)
	const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
	const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER)
	const [noShortlist, setNoShortlist] = useState(0)
	const [isFetched, setIsFetched] = useState(false)
	const bootRunIdRef = useRef(0)
	const hasFinderSelectionRef = useRef(hasFinderSelection)
	const hasSelectedCourseRef = useRef(hasSelectedCourse)
	hasFinderSelectionRef.current = hasFinderSelection
	hasSelectedCourseRef.current = hasSelectedCourse

	const shortCards = useMemo(() => {
		return fullList.map(item => ({
			...item,
			courses: item.courses || {},
		}))
	}, [fullList])

	const draftDefaults = researchSessionStore.draftDefaults || EMPTY_DRAFT_DEFAULTS

	const fetchHistory = useCallback(async () => {
		setUniversitiesError(null)
		const res = await getAllShortlisters()
		const list = Array.isArray(res?.data) ? res.data : []
		const mapped = list.map((obj, index) => ({
			id: obj.id,
			name: `Shortlist #${index + 1}`,
			query: obj.query,
			date: obj.date_created,
		}))
		if (mapped.length) {
			const last = mapped[mapped.length - 1]
			setLastShortlistId(last.id)
			return { list: mapped, last }
		}
		setLastShortlistId(null)
		return { list: mapped, last: null }
	}, [])

	const getShortlister = useCallback(
		async (id, page = 1, nextSortBy, nextSortOrder) => {
			if (!id) return
			setLoadingUniversities(true)
			setUniversitiesError(null)
			try {
				const res = nextSortBy
					? await getSortShortlisterById(id, page, nextSortBy, nextSortOrder)
					: await getShortlisterById(id, page)
				const universities = Array.isArray(res?.data?.universities)
					? res.data.universities
					: []
				const shortlistersWithData = await Promise.all(
					universities.map(async el => {
						const courses = await getAllCoursesFromShortlistUni(id, el.id)
						const firstCourse = Array.isArray(courses?.data)
							? courses.data[0]
							: null
						if (!firstCourse) return null
						return {
							...el,
							courses: firstCourse,
							coursesList: Array.isArray(courses?.data) ? courses.data : [],
						}
					}),
				)
				const visibleShortlisters = shortlistersWithData.filter(Boolean)
				setFullList(prev =>
					page === 1
						? visibleShortlisters
						: [
								...prev,
								...visibleShortlisters.filter(
									next => !prev.some(item => item.id === next.id),
								),
							],
				)
				setNoShortlist(visibleShortlisters.length)
				setCurrentPage(res?.data?.pagination?.current_page || 1)
				setTotalPages(res?.data?.pagination?.total_pages || 1)
				setIsButtonDisabled(
					(res?.data?.pagination?.current_page || 1) >=
						(res?.data?.pagination?.total_pages || 1),
				)
				setIsDisableSortBtn(false)
				setCurrentListId(id)
			} catch (error) {
				setUniversitiesError(error)
			} finally {
				setLoadingUniversities(false)
			}
		},
		[],
	)

	const openLastShortlist = useCallback(
		async last => {
			if (!last?.id) {
				setCurrentListId(null)
				setFullList([])
				setNoShortlist(0)
				setNewData(SHORTLIST_FILTER_DEFAULTS)
				return false
			}
			const filters = filtersFromShortlistQuery(last.query)
			setNewData(filters)
			setSearchDisabled(true)
			await getShortlister(last.id, 1, DEFAULT_SORT_BY, DEFAULT_SORT_ORDER)
			return true
		},
		[getShortlister],
	)

	const createShortlistFromFinderSelection = useCallback(async () => {
		const payload = buildShortlistPayloadFromDraft(
			researchSessionStore.draftDefaults || EMPTY_DRAFT_DEFAULTS,
			{ hasSelectedCourse: hasSelectedCourseRef.current },
		)
		setNewData(payload)
		setSearchDisabled(true)
		setLoadingUniversities(true)
		setUniversitiesError(null)
		setFullList([])
		try {
			const result = await createShortlist({
				...payload,
				isDeFault: false,
			})
			if (result?.status === 200 && result?.data?.id) {
				await getShortlister(
					result.data.id,
					1,
					DEFAULT_SORT_BY,
					DEFAULT_SORT_ORDER,
				)
				await fetchHistory().catch(() => {})
				return true
			}
			return false
		} catch (error) {
			setUniversitiesError(error)
			return false
		} finally {
			setLoadingUniversities(false)
		}
	}, [getShortlister, fetchHistory])

	// Bootstrap Colleges once per entry: last shortlist vs create-from-finder.
	useEffect(() => {
		if (activeScreen !== 'college') {
			setIsFetched(false)
			return undefined
		}

		const runId = ++bootRunIdRef.current
		let cancelled = false
		const isStale = () => cancelled || runId !== bootRunIdRef.current

		const run = async () => {
			setLoadingUniversities(true)
			setUniversitiesError(null)
			try {
				if (hasFinderSelectionRef.current) {
					if (isStale()) return
					const ok = await createShortlistFromFinderSelection()
					if (!isStale()) setIsFetched(ok)
					return
				}

				const { last } = await fetchHistory()
				if (isStale()) return
				const opened = await openLastShortlist(last)
				if (!isStale()) setIsFetched(opened || !last)
			} catch (error) {
				if (!isStale()) setUniversitiesError(error)
			} finally {
				if (!isStale()) setLoadingUniversities(false)
			}
		}

		run().catch(() => {})

		return () => {
			cancelled = true
		}
	}, [
		activeScreen,
		createShortlistFromFinderSelection,
		fetchHistory,
		openLastShortlist,
	])
	const onFilterChange = useCallback((key, value) => {
		setSearchDisabled(false)
		setNewData(prev => ({
			...prev,
			[key]: value,
		}))
	}, [])

	const onSearch = useCallback(async () => {
		setLoadingUniversities(true)
		setSearchDisabled(true)
		setFullList([])
		setUniversitiesError(null)
		try {
			const payload = normalizeShortlistQuery(
				withDegreeLevelForShortlist({
					...SHORTLIST_FILTER_DEFAULTS,
					...newData,
				}),
				{ isStudent: false },
			)
			const result = await createShortlist({
				...payload,
				shortlist_id: currentListId,
				isDeFault: false,
			})
			if (result?.status === 200 && result?.data?.id) {
				await getShortlister(
					result.data.id,
					1,
					sortBy || DEFAULT_SORT_BY,
					sortOrder || DEFAULT_SORT_ORDER,
				)
				await fetchHistory().catch(() => {})
			}
		} catch (error) {
			setUniversitiesError(error)
		} finally {
			setSearchDisabled(false)
			setLoadingUniversities(false)
		}
	}, [newData, currentListId, getShortlister, fetchHistory, sortBy, sortOrder])

	const onSortChange = useCallback(
		async option => {
			if (!currentListId) return
			setSortBy(option.sortBy)
			setSortOrder(option.sortOrder)
			setIsDisableSortBtn(true)
			setFullList([])
			await getShortlister(currentListId, 1, option.sortBy, option.sortOrder)
		},
		[currentListId, getShortlister],
	)

	const onShowMore = useCallback(async () => {
		if (!currentListId || currentPage >= totalPages) return
		await getShortlister(currentListId, currentPage + 1, sortBy, sortOrder)
	}, [currentListId, currentPage, totalPages, getShortlister, sortBy, sortOrder])

	const retryUniversities = useCallback(async () => {
		if (loadingUniversities || !currentListId) return
		await getShortlister(currentListId, currentPage || 1, sortBy, sortOrder)
	}, [
		loadingUniversities,
		currentListId,
		getShortlister,
		currentPage,
		sortBy,
		sortOrder,
	])

	const resetUniversities = useCallback(() => {
		setFullList([])
		setNoShortlist(0)
		setCurrentPage(1)
		setTotalPages(1)
		setUniversitiesError(null)
	}, [])

	const showPlaceholders =
		activeScreen === 'college' &&
		!loadingUniversities &&
		!currentListId &&
		!hasFinderSelection &&
		!selectedStudentId &&
		!hasSeedDefaults(draftDefaults)

	return {
		newData,
		setNewData,
		shortCards,
		loadingUniversities,
		universitiesError,
		searchDisabled,
		isDisableSortBtn,
		sortBy,
		sortOrder,
		noShortlist,
		showPlaceholders,
		isButtonDisabled,
		sortOptions: SORT_OPTIONS,
		onFilterChange,
		setSearchDisabled,
		onSearch,
		onSortChange,
		onShowMore,
		retryUniversities,
		resetUniversities,
		isFetched,
	}
}
